<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import KnowledgeBaseDocumentsEmptyState from 'dashboard/components-next/pilot/pageComponents/emptyStates/KnowledgeBaseDocumentsEmptyState.vue';
import EditKnowledgeBaseDocumentDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBaseDocument/EditKnowledgeBaseDocumentDialog.vue';
import DocumentListControls from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/DocumentListControls.vue';
import KnowledgeBaseDocumentsFilter from 'dashboard/components-next/filter/KnowledgeBaseDocumentsFilter.vue';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import PilotDocumentsAPI from 'dashboard/api/pilot/documents';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/pilot/pageComponents/BulkDeleteDialog.vue';

const props = defineProps({
  knowledgeBase: {
    type: Object,
    required: true,
  },
  initialDocuments: {
    type: Array,
    default: () => [],
  },
  initialMeta: {
    type: Object,
    default: () => ({}),
  },
  uploadDialogRef: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['refresh', 'upload']);

const store = useStore();
const { t } = useI18n();

const knowledgeBaseId = computed(() => props.knowledgeBase.id);

// 文档列表状态
const documents = ref(props.initialDocuments);
const currentPage = ref(1);
const documentsMeta = ref({
  totalCount: props.initialMeta.total_count || 0,
  currentPage: props.initialMeta.current_page || 1,
  totalPages: props.initialMeta.total_pages || 1,
  perPage: props.initialMeta.per_page || 25,
});

// 搜索、筛选、排序状态
const searchValue = ref('');
const sortBy = ref('created_at');
const sortOrder = ref('-');
const isFilterOpen = ref(false);
const appliedFilters = ref([]);
const filterQuery = ref([]);

let filterIdCounter = 0;
const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'contains',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

// 选中的文档
const selectedDocument = ref(null);
const editDocumentDialog = ref(null);
const deleteDocumentDialog = ref(null);

// 批量选择状态
const bulkSelectedIds = ref(new Set());
const hoveredRow = ref(null);

// 批量操作状态
const isBulkRetrying = ref(false);
const isRetrying = ref({});

// 计算属性
const isSearching = computed(() => searchValue.value.trim() !== '');
const isFiltering = computed(() => appliedFilters.value.length > 0);
const hasDocuments = computed(
  () => documents.value && documents.value.length > 0
);
const showEmptyState = computed(
  () => !isSearching.value && !isFiltering.value && !hasDocuments.value
);
const showNoResults = computed(
  () => (isSearching.value || isFiltering.value) && !hasDocuments.value
);

// 获取文档列表
const fetchDocuments = async (page = 1) => {
  const params = { page };

  if (searchValue.value) {
    params.search = searchValue.value;
  }

  if (sortBy.value) {
    const sortPrefix = sortOrder.value === '-' ? '-' : '';
    params.sort = `${sortPrefix}${sortBy.value}`;
  }

  try {
    let response;

    if (isFiltering.value) {
      const snakeCaseFilters = useSnakeCase(
        JSON.parse(JSON.stringify(appliedFilters.value))
      );
      const filterPayload = filterQueryGenerator(snakeCaseFilters);

      const { data } = await PilotDocumentsAPI.filter({
        knowledgeBaseId: knowledgeBaseId.value,
        page,
        search: searchValue.value,
        sort: params.sort,
        payload: filterPayload.payload,
      });

      response = data;
    } else {
      response = await store.dispatch('pilotKnowledgeBases/show', {
        id: knowledgeBaseId.value,
        ...params,
      });
    }

    const docs = response.payload?.documents || [];
    documents.value = docs;

    if (response.meta) {
      documentsMeta.value = {
        totalCount: response.meta.total_count || documents.value.length,
        currentPage: response.meta.current_page || 1,
        totalPages: response.meta.total_pages || 1,
        perPage: response.meta.per_page || 25,
      };
      currentPage.value = response.meta.current_page || 1;
    }
  } catch (error) {
    console.error('Failed to fetch documents:', error);
  }
};

// 搜索防抖
let searchDebounceTimer = null;
const handleSearchChange = value => {
  searchValue.value = value;
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    fetchDocuments(1);
  }, 500);
};

// 排序和筛选处理
const handleSortChange = ({ sort, order }) => {
  sortBy.value = sort;
  sortOrder.value = order;
  fetchDocuments(currentPage.value);
};

// 筛选相关
const handleToggleFilter = () => {
  // 打开筛选面板时，如果没有筛选条件，添加一个默认的
  const shouldOpen = !isFilterOpen.value;
  isFilterOpen.value = shouldOpen;

  if (shouldOpen && filterQuery.value.length === 0) {
    filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  }
};

const handleCloseFilter = () => {
  isFilterOpen.value = false;
};

const handleApplyFilter = filters => {
  appliedFilters.value = filters;
  isFilterOpen.value = false; // 应用后关闭面板
  fetchDocuments(1);
};

const handleClearAllFilters = () => {
  appliedFilters.value = [];
  filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }]; // 重置为默认筛选
  fetchDocuments(1);
};

// 文档操作
const handleUploadDocument = () => {
  emit('upload');
};

const handleEditDocument = async doc => {
  selectedDocument.value = doc;
  await nextTick();
  editDocumentDialog.value?.open();
};

const handleDeleteDocument = async doc => {
  selectedDocument.value = doc;
  await nextTick();
  deleteDocumentDialog.value?.dialogRef?.open();
};

const handleRetryDocument = async doc => {
  isRetrying.value[doc.id] = true;
  try {
    const response = await PilotDocumentsAPI.retry(doc.id);
    const updatedDoc = response.data.payload || response.data;

    const index = documents.value.findIndex(d => d.id === doc.id);
    if (index !== -1 && updatedDoc) {
      documents.value[index] = {
        ...documents.value[index],
        status: updatedDoc.status,
        content: updatedDoc.content,
        file_size: updatedDoc.file_size,
        chunks_count: updatedDoc.chunks_count,
        character_count: updatedDoc.character_count,
        metadata: updatedDoc.metadata,
        creator: updatedDoc.creator,
      };
    }

    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY_SUCCESS'));
  } catch (error) {
    console.error('Failed to retry document:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY_ERROR'));
  } finally {
    isRetrying.value[doc.id] = false;
  }
};

const canRetryDocument = doc => {
  return (
    doc.status === 'available' ||
    doc.status === 'crawl_failed' ||
    doc.status === 'chunk_failed' ||
    doc.status === 'embedding_failed'
  );
};

// 删除成功回调
const onDeleteSuccess = () => {
  if (selectedDocument.value) {
    documents.value = documents.value.filter(
      doc => doc.id !== selectedDocument.value.id
    );
    selectedDocument.value = null;
  }
};

// 编辑成功回调
const onEditSuccess = updatedDoc => {
  if (selectedDocument.value && updatedDoc) {
    const index = documents.value.findIndex(
      doc => doc.id === selectedDocument.value.id
    );
    if (index !== -1) {
      documents.value[index] = {
        ...documents.value[index],
        name: updatedDoc.name,
      };
    }
    selectedDocument.value = null;
  }
};

// 批量选择
const buildSelectedCountLabel = computed(() => {
  const count = documents.value.length;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('PILOT.KNOWLEDGE_BASES.UNSELECT_ALL', { count })
    : t('PILOT.KNOWLEDGE_BASES.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('PILOT.KNOWLEDGE_BASES.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRowHover = (isHovered, id) => {
  hoveredRow.value = isHovered ? id : null;
};

const handleRowSelect = (id, isSelected) => {
  const selected = new Set(bulkSelectedIds.value);
  if (isSelected) {
    selected.add(id);
  } else {
    selected.delete(id);
  }
  bulkSelectedIds.value = selected;
};

// 批量重试
const handleBulkRetry = async () => {
  if (bulkSelectedIds.value.size === 0) return;

  isBulkRetrying.value = true;

  try {
    const ids = Array.from(bulkSelectedIds.value);
    let successCount = 0;
    let failCount = 0;

    for (const id of ids) {
      try {
        const response = await PilotDocumentsAPI.retry(id);
        const updatedDoc = response.data.payload || response.data;

        const index = documents.value.findIndex(d => d.id === id);
        if (index !== -1 && updatedDoc) {
          documents.value[index] = {
            ...documents.value[index],
            status: updatedDoc.status,
            content: updatedDoc.content,
            file_size: updatedDoc.file_size,
            chunks_count: updatedDoc.chunks_count,
            character_count: updatedDoc.character_count,
            metadata: updatedDoc.metadata,
          };
        }
        successCount++;
      } catch (error) {
        console.error(`Failed to retry document ${id}:`, error);
        failCount++;
      }
    }

    if (failCount === 0) {
      useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY_SUCCESS'));
    } else if (successCount === 0) {
      useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY_ERROR'));
    } else {
      useAlert(
        `${successCount} ${t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY_SUCCESS')}, ${failCount} ${t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY_ERROR')}`
      );
    }

    bulkSelectedIds.value = new Set();
  } catch (error) {
    console.error('Failed to bulk retry documents:', error);
    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY_ERROR'));
  } finally {
    isBulkRetrying.value = false;
  }
};

// 批量删除
const bulkDeleteDialog = ref(null);
const handleBulkDelete = () => {
  bulkDeleteDialog.value?.open();
};

// 批量删除成功回调
const onBulkDeleteSuccess = () => {
  const idsToDelete = Array.from(bulkSelectedIds.value);
  documents.value = documents.value.filter(
    doc => !idsToDelete.includes(doc.id)
  );
  documentsMeta.value.totalCount -= idsToDelete.length;
  bulkSelectedIds.value = new Set();

  if (documents.value.length === 0 && currentPage.value > 1) {
    fetchDocuments(currentPage.value - 1);
  }
};

// 分页
const handlePageChange = page => {
  fetchDocuments(page);
};

// 格式化文件大小
const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const size = parseFloat((bytes / k ** i).toFixed(1));
  return `${size} ${sizes[i]}`;
};

const getInitial = name => {
  if (!name) return 'U';
  return name.charAt(0).toUpperCase();
};

const getCreatorColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = name?.charCodeAt(0) || 0;
  return colors[index % colors.length];
};

const getFileExtension = doc => {
  if (doc.file_type) {
    return doc.file_type;
  }

  if (doc.external_link && !doc.external_link.startsWith('PDF:')) {
    return 'url';
  }

  if (doc.name) {
    const match = doc.name.toLowerCase().match(/\.(\w+)$/);
    if (match) {
      const ext = match[1];
      const supportedExtensions = ['pdf', 'txt', 'md', 'docx', 'pptx'];
      if (supportedExtensions.includes(ext)) {
        return ext;
      }
    }
  }

  return 'txt';
};

const getStatusText = status => {
  return t(`PILOT.KNOWLEDGE_BASES.DOCUMENTS.STATUS_${status.toUpperCase()}`);
};

const getStatusConfig = status => {
  const configMap = {
    processing: {
      bgColor: 'bg-n-blue-1 dark:bg-n-blue-2',
      textColor: 'text-n-blue-10 dark:text-n-blue-9',
      dotColor: 'bg-n-blue-5',
    },
    crawling: {
      bgColor: 'bg-n-blue-1 dark:bg-n-blue-2',
      textColor: 'text-n-blue-10 dark:text-n-blue-9',
      dotColor: 'bg-n-blue-5',
    },
    chunking: {
      bgColor: 'bg-n-blue-1 dark:bg-n-blue-2',
      textColor: 'text-n-blue-10 dark:text-n-blue-9',
      dotColor: 'bg-n-blue-5',
    },
    embedding: {
      bgColor: 'bg-n-blue-1 dark:bg-n-blue-2',
      textColor: 'text-n-blue-10 dark:text-n-blue-9',
      dotColor: 'bg-n-blue-5',
    },
    in_progress: {
      bgColor: 'bg-n-blue-1 dark:bg-n-blue-2',
      textColor: 'text-n-blue-10 dark:text-n-blue-9',
      dotColor: 'bg-n-blue-5',
    },
    available: {
      bgColor: 'bg-n-teal-1 dark:bg-n-teal-2',
      textColor: 'text-n-teal-10 dark:text-n-teal-9',
      dotColor: 'bg-n-teal-5',
    },
    failed: {
      bgColor: 'bg-n-ruby-1 dark:bg-n-ruby-2',
      textColor: 'text-n-ruby-10 dark:text-n-ruby-9',
      dotColor: 'bg-n-ruby-5',
    },
    crawl_failed: {
      bgColor: 'bg-n-ruby-1 dark:bg-n-ruby-2',
      textColor: 'text-n-ruby-10 dark:text-n-ruby-9',
      dotColor: 'bg-n-ruby-5',
    },
    chunk_failed: {
      bgColor: 'bg-n-ruby-1 dark:bg-n-ruby-2',
      textColor: 'text-n-ruby-10 dark:text-n-ruby-9',
      dotColor: 'bg-n-ruby-5',
    },
    embedding_failed: {
      bgColor: 'bg-n-ruby-1 dark:bg-n-ruby-2',
      textColor: 'text-n-ruby-10 dark:text-n-ruby-9',
      dotColor: 'bg-n-ruby-5',
    },
  };

  const config = configMap[status];
  return (
    config || {
      bgColor: 'bg-n-slate-1 dark:bg-n-slate-2',
      textColor: 'text-n-slate-10 dark:text-n-slate-9',
      dotColor: 'bg-n-slate-5',
    }
  );
};

const getFailureReason = doc => {
  if (
    doc.status !== 'chunk_failed' &&
    doc.status !== 'crawl_failed' &&
    doc.status !== 'embedding_failed'
  ) {
    return '';
  }

  const failureInfo = doc.metadata?.failure_info;
  if (!failureInfo) {
    return t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.UNKNOWN_ERROR');
  }

  return (
    failureInfo.message || t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.UNKNOWN_ERROR')
  );
};

// 监听知识库变化，刷新列表
watch(
  () => props.knowledgeBase,
  () => {
    fetchDocuments(1);
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- 批量操作条 -->
    <div class="flex-shrink-0 my-2 relative">
      <BulkSelectBar
        v-if="hasDocuments || isSearching || isFiltering"
        v-model="bulkSelectedIds"
        :all-items="documents"
        :select-all-label="buildSelectedCountLabel"
        :selected-count-label="selectedCountLabel"
        :delete-label="$t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.BULK_DELETE_BUTTON')"
        class="w-full"
      >
        <template #actions>
          <div class="flex items-center gap-2">
            <Button
              :label="$t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY')"
              icon="i-lucide-refresh-cw"
              size="sm"
              ghost
              blue
              :is-loading="isBulkRetrying"
              @click="handleBulkRetry"
            />
            <Button
              :label="$t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.BULK_DELETE_BUTTON')"
              icon="i-lucide-trash"
              size="sm"
              ghost
              blue
              @click="handleBulkDelete"
            />
          </div>
        </template>
        <template #default-actions>
          <div class="flex items-center justify-between w-full">
            <DocumentListControls
              key="list-controls"
              :search-value="searchValue"
              :sort-by="sortBy"
              :sort-order="sortOrder"
              :is-filter-open="isFilterOpen"
              :show-filter="true"
              @update:search-value="handleSearchChange"
              @update:sort="handleSortChange"
              @click-filter="handleToggleFilter"
            />
          </div>
        </template>
      </BulkSelectBar>

      <!-- 筛选面板（绝对定位弹出） -->
      <div v-if="isFilterOpen" class="absolute top-full left-0 z-50 mt-2">
        <KnowledgeBaseDocumentsFilter
          v-model="filterQuery"
          @close="handleCloseFilter"
          @apply-filter="handleApplyFilter"
          @clear-filters="handleClearAllFilters"
        />
      </div>
    </div>

    <!-- 内容区域：空状态/无结果/文档列表 + 分页 -->
    <div class="flex-1 overflow-hidden flex flex-col">
      <!-- 空状态 -->
      <KnowledgeBaseDocumentsEmptyState
        v-if="showEmptyState"
        :knowledge-base="knowledgeBase"
        @click="handleUploadDocument"
      />

      <!-- 无结果 -->
      <div
        v-else-if="showNoResults"
        class="flex flex-col items-center justify-center py-20"
      >
        <div class="i-lucide-search-x h-16 w-16 text-n-slate-5 mb-4" />
        <p class="text-n-slate-11 text-lg font-medium">
          {{ $t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.NO_RESULTS') }}
        </p>
        <p class="text-n-slate-10 text-sm mt-2">
          {{ $t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TRY_DIFFERENT_SEARCH') }}
        </p>
      </div>

      <!-- 文档列表 + 分页 -->
      <div v-else class="flex flex-col h-full">
        <!-- 表格区域 -->
        <div class="flex-1 overflow-auto">
          <table class="min-w-[1180px] divide-y divide-n-weak table-fixed">
            <thead>
              <tr>
                <th class="py-3 pl-4 w-[40px]" />
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[350px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_NAME') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[140px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_STATUS') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[200px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_ERROR') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[100px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_FILE_SIZE') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[80px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_CHUNKS_COUNT') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[100px]"
                >
                  {{
                    t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_CHARACTER_COUNT')
                  }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[150px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_CREATOR') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-left text-n-slate-11 whitespace-nowrap w-[160px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_CREATED_AT') }}
                </th>
                <th
                  class="py-3 px-4 font-semibold text-right text-n-slate-11 whitespace-nowrap w-[100px]"
                >
                  {{ t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.TABLE_ACTIONS') }}
                </th>
              </tr>
            </thead>

            <tbody class="divide-y divide-n-weak text-n-slate-11">
              <tr
                v-for="doc in documents"
                :key="doc.id"
                class="hover:bg-n-alpha-1 transition-colors duration-200"
                @mouseenter="handleRowHover(true, doc.id)"
                @mouseleave="handleRowHover(false, doc.id)"
              >
                <!-- Checkbox -->
                <td class="py-4 pl-4 align-middle">
                  <div
                    v-show="hoveredRow === doc.id || bulkSelectedIds.size > 0"
                    class="scale-125 origin-left"
                  >
                    <Checkbox
                      :model-value="bulkSelectedIds.has(doc.id)"
                      @update:model-value="
                        isSelected => handleRowSelect(doc.id, isSelected)
                      "
                      @click.stop
                    />
                  </div>
                </td>

                <!-- 文档名称 -->
                <td class="py-4 px-4 align-middle">
                  <div class="flex items-center gap-2">
                    <div class="h-7 w-7 text-n-slate-5 flex-shrink-0">
                      <FileIcon
                        v-if="getFileExtension(doc)"
                        :file-type="getFileExtension(doc)"
                        class="h-7 w-7"
                      />
                      <div v-else class="i-lucide-file-text h-7 w-7" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="font-medium text-n-slate-12 truncate">
                        {{ doc.name }}
                      </div>
                      <div
                        v-if="doc.content"
                        class="text-sm text-n-slate-11 line-clamp-1 mt-1"
                      >
                        {{ doc.content }}
                      </div>
                      <div
                        v-else-if="doc.external_link"
                        class="text-sm text-n-slate-11 line-clamp-1 mt-1"
                      >
                        {{ doc.external_link }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- 状态 -->
                <td class="py-4 px-4 align-middle">
                  <div
                    v-if="doc.status"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium max-w-[140px]"
                    :class="[
                      getStatusConfig(doc.status).bgColor,
                      getStatusConfig(doc.status).textColor,
                    ]"
                    :title="getStatusText(doc.status)"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :class="[getStatusConfig(doc.status).dotColor]"
                    />
                    <span class="truncate">{{
                      getStatusText(doc.status)
                    }}</span>
                  </div>
                </td>

                <!-- 错误信息 -->
                <td class="py-4 px-4 align-middle w-[200px]">
                  <div
                    v-if="getFailureReason(doc)"
                    class="text-xs text-n-red-11 line-clamp-2"
                    :title="getFailureReason(doc)"
                  >
                    {{ getFailureReason(doc) }}
                  </div>
                  <span v-else class="text-sm text-n-slate-10">-</span>
                </td>

                <!-- 文件大小 -->
                <td class="py-4 px-4 align-middle whitespace-nowrap">
                  <span v-if="doc.file_size" class="text-sm text-n-slate-11">
                    {{ formatFileSize(doc.file_size) }}
                  </span>
                  <span v-else class="text-sm text-n-slate-10">-</span>
                </td>

                <!-- 分片数 -->
                <td class="py-4 px-4 align-middle whitespace-nowrap">
                  <span class="text-sm text-n-slate-11">
                    {{ doc.chunks_count || 0 }}
                  </span>
                </td>

                <!-- 字符数 -->
                <td class="py-4 px-4 align-middle whitespace-nowrap">
                  <span class="text-sm text-n-slate-11">
                    {{ doc.character_count || 0 }}
                  </span>
                </td>

                <!-- 创建人 -->
                <td class="py-4 px-4 align-middle whitespace-nowrap">
                  <div class="flex items-center gap-2">
                    <div
                      class="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
                      :style="{
                        backgroundColor: getCreatorColor(doc.creator?.name),
                      }"
                    >
                      {{ getInitial(doc.creator?.name) }}
                    </div>
                    <span class="text-sm text-n-slate-11">
                      {{ doc.creator?.name || 'Unknown' }}
                    </span>
                  </div>
                </td>

                <!-- 创建时间 -->
                <td class="py-4 px-4 align-middle whitespace-nowrap">
                  <span class="text-sm text-n-slate-11">
                    {{ dynamicTime(doc.created_at) }}
                  </span>
                </td>

                <!-- 操作 -->
                <td class="py-4 px-4 align-middle whitespace-nowrap">
                  <div class="flex items-center justify-end gap-1">
                    <Button
                      v-if="canRetryDocument(doc)"
                      v-tooltip.top="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.RETRY')"
                      icon="i-lucide-refresh-cw"
                      slate
                      xs
                      faded
                      :is-loading="isRetrying[doc.id]"
                      @click="handleRetryDocument(doc)"
                    />
                    <Button
                      v-tooltip.top="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.EDIT')"
                      icon="i-lucide-pen"
                      slate
                      xs
                      faded
                      @click="handleEditDocument(doc)"
                    />
                    <Button
                      v-tooltip.top="t('PILOT.KNOWLEDGE_BASES.OPTIONS.DELETE')"
                      icon="i-lucide-trash-2"
                      xs
                      ruby
                      faded
                      @click="handleDeleteDocument(doc)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页 - 固定在底部 -->
        <footer
          v-if="documentsMeta.totalCount > 0"
          class="flex-shrink-0 sticky bottom-0 z-10 px-4 pb-4 mt-2 bg-n-slate-white dark:bg-n-slate-800 border-t border-n-weak"
        >
          <PaginationFooter
            :current-page="documentsMeta.currentPage"
            :total-items="documentsMeta.totalCount"
            :items-per-page="documentsMeta.perPage"
            @update:current-page="handlePageChange"
          />
        </footer>
      </div>
    </div>

    <!-- 对话框 -->
    <EditKnowledgeBaseDocumentDialog
      ref="editDocumentDialog"
      :document="selectedDocument"
      @success="onEditSuccess"
    />

    <DeleteDialog
      ref="deleteDocumentDialog"
      :entity="{ id: selectedDocument?.id, name: selectedDocument?.name }"
      type="Document"
      translation-key="KNOWLEDGE_BASES.DOCUMENTS"
      @delete-success="onDeleteSuccess"
    />

    <BulkDeleteDialog
      ref="bulkDeleteDialog"
      :bulk-ids="bulkSelectedIds"
      type="Document"
      translation-key="KNOWLEDGE_BASES.DOCUMENTS"
      @delete-success="onBulkDeleteSuccess"
    />
  </div>
</template>
